import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import VideocamIcon from '@mui/icons-material/Videocam';
import CloseIcon from '@mui/icons-material/Close';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import useStore from '../store/useStore';
import useIsMobile from '../hooks/useIsMobile';
import {
  MAX_VIDEO_BYTES,
  STOP_VIDEO_BYTES,
  WARN_VIDEO_BYTES,
  canRecordInBrowser,
  clipFilename,
  formatDuration,
  formatSize,
  pickRecorderMimeType,
} from './video/videoApi';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
`;

const VIDEO_BITS_PER_SECOND = 700000;
const AUDIO_BITS_PER_SECOND = 64000;

function cameraErrorText(err, en) {
  const name = err && err.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return en
      ? 'Camera or microphone access is blocked. Allow it in the browser settings (the icon next to the address bar) and try again.'
      : 'Доступ к камере или микрофону запрещён. Разрешите его в настройках браузера (значок слева от адреса) и попробуйте снова.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return en
      ? 'No camera or microphone was found on this device.'
      : 'Камера или микрофон не найдены на этом устройстве.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return en
      ? 'The camera is busy in another app or tab. Close it and try again.'
      : 'Камера занята другим приложением или вкладкой. Закройте его и попробуйте снова.';
  }
  return en ? 'Could not start the camera.' : 'Не удалось запустить камеру.';
}

function stopTracks(stream) {
  if (!stream) return;
  stream.getTracks().forEach((track) => track.stop());
}

// Records a clip from the camera (or takes a picked video file) and hands it
// to `onClip({ blob, filename, title, durationSec })`. Nothing is uploaded here.
// While recording, the panel collapses into a small floating bar so the page
// underneath stays usable. `onBusyChange(true)` is reported while a recording
// or an undecided clip exists, so the host can hold off closing.
export default function AssessmentVideoRecorder({
  onClip,
  onBusyChange,
  onPanelOpenChange,
  onOpen,
  disabled = false,
  maxMinutes = 10,
  trigger = 'icon',
  triggerLabel,
  floatingBottom = 16,
}) {
  const { language } = useStore();
  const en = language === 'en';
  const isMobile = useIsMobile();
  const supported = canRecordInBrowser();
  const mimeType = pickRecorderMimeType();
  const canPause =
    typeof MediaRecorder !== 'undefined' &&
    !!MediaRecorder.prototype &&
    typeof MediaRecorder.prototype.pause === 'function';

  const [panel, setPanel] = useState(false);
  // 'idle' | 'starting' | 'ready' | 'recording' | 'paused' | 'review'
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [facing, setFacing] = useState('environment');
  const [canSwitch, setCanSwitch] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [recBytes, setRecBytes] = useState(0);
  const [review, setReview] = useState(null); // { blob, url, filename, durationSec, fromFile }
  const [title, setTitle] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const sizeRef = useRef(0);
  const baseMsRef = useRef(0);
  const segStartRef = useRef(0);
  const timerRef = useRef(null);
  const liveRef = useRef(null);
  const startTokenRef = useRef(0);
  const stopReasonRef = useRef('');
  const phaseRef = useRef('idle');
  const fileInputRef = useRef(null);
  const captureInputRef = useRef(null);

  const changePhase = (next) => {
    phaseRef.current = next;
    setPhase(next);
  };

  const attachLive = useCallback((el) => {
    if (!el) return;
    liveRef.current = el;
    if (streamRef.current && el.srcObject !== streamRef.current) {
      el.srcObject = streamRef.current;
    }
  }, []);

  const releaseStream = () => {
    startTokenRef.current += 1;
    stopTracks(streamRef.current);
    streamRef.current = null;
    if (liveRef.current) liveRef.current.srcObject = null;
  };

  const clearTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const currentElapsedMs = () =>
    baseMsRef.current +
    (segStartRef.current ? Date.now() - segStartRef.current : 0);

  const startPreview = async (facingMode) => {
    releaseStream();
    const token = startTokenRef.current;
    setError('');
    changePhase('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 },
        },
        audio: true,
      });
      if (token !== startTokenRef.current) {
        stopTracks(stream);
        return;
      }
      streamRef.current = stream;
      if (liveRef.current) liveRef.current.srcObject = stream;
      changePhase('ready');
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (token === startTokenRef.current) {
          setCanSwitch(
            devices.filter((d) => d.kind === 'videoinput').length > 1
          );
        }
      } catch (e) {
        /* switch button stays hidden */
      }
    } catch (err) {
      if (token !== startTokenRef.current) return;
      setError(cameraErrorText(err, en));
      changePhase('idle');
    }
  };

  const finishRecording = (recorder) => {
    clearTimer();
    const durationSec = Math.round(currentElapsedMs() / 1000);
    segStartRef.current = 0;
    const type = recorder.mimeType || mimeType || 'video/webm';
    const blob = new Blob(chunksRef.current, { type });
    chunksRef.current = [];
    recorderRef.current = null;
    releaseStream();
    if (!blob.size) {
      setError(
        en
          ? 'The recording is empty. Try again.'
          : 'Запись пустая. Попробуйте ещё раз.'
      );
      changePhase('idle');
      setPanel(true);
      return;
    }
    if (stopReasonRef.current) setNotice(stopReasonRef.current);
    stopReasonRef.current = '';
    setTitle('');
    setConfirmDiscard(false);
    setReview({
      blob,
      url: URL.createObjectURL(blob),
      filename: clipFilename(blob),
      durationSec,
      fromFile: false,
    });
    changePhase('review');
    setPanel(true);
  };

  const stopRecording = (reason = '') => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    stopReasonRef.current = reason;
    try {
      recorder.stop();
    } catch (e) {
      finishRecording(recorder);
    }
  };

  const tick = () => {
    const ms = currentElapsedMs();
    setElapsed(Math.floor(ms / 1000));
    setRecBytes(sizeRef.current);
    if (phaseRef.current !== 'recording') return;
    if (ms >= maxMinutes * 60 * 1000) {
      stopRecording(
        en
          ? `Recording stopped: the ${maxMinutes}-minute limit was reached.`
          : `Запись остановлена: достигнут лимит ${maxMinutes} мин.`
      );
    } else if (sizeRef.current >= STOP_VIDEO_BYTES) {
      stopRecording(
        en
          ? 'Recording stopped: the file is about to reach the size limit.'
          : 'Запись остановлена: файл почти достиг предельного размера.'
      );
    }
  };

  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;
    let recorder;
    try {
      recorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
    } catch (e) {
      try {
        recorder = new MediaRecorder(stream);
      } catch (err) {
        setError(
          en
            ? 'This browser cannot record video. Pick a file instead.'
            : 'Этот браузер не может записывать видео. Выберите файл.'
        );
        return;
      }
    }
    chunksRef.current = [];
    sizeRef.current = 0;
    setRecBytes(0);
    baseMsRef.current = 0;
    segStartRef.current = Date.now();
    stopReasonRef.current = '';
    setNotice('');
    setElapsed(0);
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size) {
        chunksRef.current.push(e.data);
        sizeRef.current += e.data.size;
      }
    };
    recorder.onstop = () => finishRecording(recorder);
    recorder.onerror = () => {
      stopReasonRef.current = en
        ? 'The recording was interrupted by an error.'
        : 'Запись прервана из-за ошибки.';
      try {
        if (recorder.state !== 'inactive') recorder.stop();
      } catch (e) {
        finishRecording(recorder);
      }
    };
    recorderRef.current = recorder;
    try {
      recorder.start(1000);
    } catch (e) {
      recorderRef.current = null;
      clearTimer();
      segStartRef.current = 0;
      setError(en ? 'Could not start recording.' : 'Не удалось начать запись.');
      return;
    }
    changePhase('recording');
    setPanel(false);
    clearTimer();
    timerRef.current = setInterval(tick, 250);
  };

  const togglePause = () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    try {
      if (phaseRef.current === 'recording') {
        recorder.pause();
        baseMsRef.current = currentElapsedMs();
        segStartRef.current = 0;
        changePhase('paused');
      } else if (phaseRef.current === 'paused') {
        recorder.resume();
        segStartRef.current = Date.now();
        changePhase('recording');
      }
    } catch (e) {
      /* pause unsupported — keep recording */
    }
  };

  const clearReview = () => {
    setReview((prev) => {
      if (prev && prev.url) URL.revokeObjectURL(prev.url);
      return null;
    });
    setConfirmDiscard(false);
    setTitle('');
  };

  const closePanel = () => {
    if (phaseRef.current === 'review') return;
    releaseStream();
    changePhase('idle');
    setPanel(false);
    setError('');
  };

  const openPanel = () => {
    if (disabled) return;
    setNotice('');
    setError('');
    setPanel(true);
    if (onOpen) onOpen();
  };

  // Camera preview while the panel is open and nothing else is going on
  useEffect(() => {
    if (!panel || phase !== 'idle' || error || !supported) return;
    startPreview(facing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel, phase, supported]);

  const switchCamera = () => {
    const next = facing === 'environment' ? 'user' : 'environment';
    setFacing(next);
    startPreview(next);
  };

  const retryCamera = () => {
    setError('');
    startPreview(facing);
  };

  const pickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.type && !file.type.startsWith('video/')) {
      setError(en ? 'That is not a video file.' : 'Это не видеофайл.');
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      setError(
        en
          ? `The file is too large (${formatSize(file.size)}). The limit is 60 MB. Record in the app instead, or pick a shorter clip.`
          : `Файл слишком большой (${formatSize(file.size)}). Предел — 60 МБ. Запишите видео прямо в приложении или выберите ролик покороче.`
      );
      return;
    }
    releaseStream();
    setError('');
    setTitle('');
    setConfirmDiscard(false);
    setReview({
      blob: file,
      url: URL.createObjectURL(file),
      filename: clipFilename(file, file.name),
      durationSec: 0,
      fromFile: true,
    });
    changePhase('review');
    setPanel(true);
  };

  const keepClip = () => {
    if (!review) return;
    const clip = {
      blob: review.blob,
      filename: review.filename,
      title: title.trim(),
      durationSec: review.durationSec,
    };
    clearReview();
    changePhase('idle');
    setPanel(false);
    if (onClip) onClip(clip);
  };

  const discardClip = () => {
    clearReview();
    changePhase('idle'); // preview restarts if the panel stays open
  };

  const recording = phase === 'recording' || phase === 'paused';
  // Time left: whichever comes first, the time cap or the size cap
  const bytesPerSec =
    elapsed >= 5 && recBytes > 0
      ? recBytes / elapsed
      : (VIDEO_BITS_PER_SECOND + AUDIO_BITS_PER_SECOND) / 8;
  const remainingSec = Math.max(
    0,
    Math.min(
      maxMinutes * 60 - elapsed,
      (STOP_VIDEO_BYTES - recBytes) / bytesPerSec
    )
  );
  const busy =
    phase === 'recording' || phase === 'paused' || phase === 'review';
  useEffect(() => {
    if (onBusyChange) onBusyChange(busy);
  }, [busy, onBusyChange]);

  const panelOpen = panel && !recording;
  useEffect(() => {
    if (onPanelOpenChange) onPanelOpenChange(panelOpen);
  }, [panelOpen, onPanelOpenChange]);

  useEffect(() => {
    if (!busy) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  // Release the camera and any preview URL when this unmounts
  useEffect(
    () => () => {
      clearTimer();
      const recorder = recorderRef.current;
      if (recorder) {
        recorder.onstop = null;
        recorder.ondataavailable = null;
        try {
          if (recorder.state !== 'inactive') recorder.stop();
        } catch (e) {
          /* already stopped */
        }
      }
      stopTracks(streamRef.current);
      streamRef.current = null;
    },
    []
  );
  const reviewUrlRef = useRef(null);
  reviewUrlRef.current = review ? review.url : null;
  useEffect(
    () => () => {
      if (reviewUrlRef.current) URL.revokeObjectURL(reviewUrlRef.current);
    },
    []
  );

  const label = triggerLabel || (en ? 'Record video' : 'Записать видео');
  const maxLabel = en ? `up to ${maxMinutes} min` : `до ${maxMinutes} мин`;

  const fileButtons = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      {!supported && (
        <Button
          variant="contained"
          startIcon={<VideocamIcon />}
          onClick={() => captureInputRef.current?.click()}
          sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
        >
          {en ? 'Shoot with the phone camera' : 'Снять на камеру телефона'}
        </Button>
      )}
      <Button
        variant="outlined"
        startIcon={<FolderOpenIcon />}
        onClick={() => fileInputRef.current?.click()}
        sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600 }}
      >
        {en ? 'Choose a video file' : 'Выбрать видеофайл'}
      </Button>
    </Stack>
  );

  return (
    <>
      {trigger === 'button' ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<VideocamIcon />}
          onClick={openPanel}
          disabled={disabled || recording}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {label}
        </Button>
      ) : (
        <Tooltip title={label}>
          <span>
            <IconButton
              color="inherit"
              onClick={openPanel}
              disabled={disabled || recording}
              aria-label={label}
              size="small"
            >
              <Badge
                color="error"
                variant="dot"
                invisible={!recording && phase !== 'review'}
              >
                <VideocamIcon />
              </Badge>
            </IconButton>
          </span>
        </Tooltip>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        hidden
        onChange={pickFile}
      />
      <input
        ref={captureInputRef}
        type="file"
        accept="video/*"
        capture="environment"
        hidden
        onChange={pickFile}
      />

      <Dialog
        open={panel && !recording}
        onClose={closePanel}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            {phase === 'review'
              ? en
                ? 'Check the clip'
                : 'Проверьте клип'
              : en
                ? 'Video recording'
                : 'Запись видео'}
          </Box>
          {phase !== 'review' && (
            <IconButton onClick={closePanel} aria-label="close" edge="end">
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          {notice && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              {notice}
            </Alert>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 1.5 }}
              action={
                supported && phase === 'idle' ? (
                  <Button color="inherit" size="small" onClick={retryCamera}>
                    {en ? 'Retry' : 'Повторить'}
                  </Button>
                ) : null
              }
            >
              {error}
            </Alert>
          )}

          {phase === 'review' && review && (
            <>
              <Box
                component="video"
                src={review.url}
                controls
                playsInline
                preload="metadata"
                onLoadedMetadata={(e) => {
                  const d = e.currentTarget.duration;
                  if (review.fromFile && Number.isFinite(d) && d > 0) {
                    setReview((prev) =>
                      prev ? { ...prev, durationSec: Math.round(d) } : prev
                    );
                  }
                }}
                sx={{
                  width: '100%',
                  maxHeight: '55vh',
                  bgcolor: '#000',
                  borderRadius: 2,
                  display: 'block',
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.75 }}
              >
                {review.durationSec
                  ? `${formatDuration(review.durationSec)} · `
                  : ''}
                {formatSize(review.blob.size)}
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                label={en ? 'Title (optional)' : 'Название (необязательно)'}
                slotProps={{ htmlInput: { maxLength: 120 } }}
                sx={{ mt: 1.5 }}
              />
            </>
          )}

          {phase !== 'review' && supported && !error && (
            <>
              <Box
                sx={{
                  position: 'relative',
                  bgcolor: '#000',
                  borderRadius: 2,
                  overflow: 'hidden',
                  aspectRatio: '16 / 9',
                  maxHeight: '55vh',
                  mx: 'auto',
                }}
              >
                <Box
                  component="video"
                  ref={attachLive}
                  autoPlay
                  muted
                  playsInline
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                {phase === 'starting' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress sx={{ color: '#fff' }} />
                  </Box>
                )}
                {phase === 'ready' && canSwitch && (
                  <IconButton
                    onClick={switchCamera}
                    aria-label={en ? 'Switch camera' : 'Сменить камеру'}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: '#fff',
                      bgcolor: 'rgba(0,0,0,0.45)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                    }}
                  >
                    <CameraswitchIcon />
                  </IconButton>
                )}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}
              >
                {en
                  ? `Sound is recorded too · ${maxLabel}`
                  : `Звук записывается · ${maxLabel}`}
              </Typography>
            </>
          )}

          {phase !== 'review' && (!supported || error) && (
            <Box sx={{ mt: error ? 0 : 1 }}>
              {!supported && !error && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {en
                    ? 'In-page recording is not available in this browser. You can shoot a clip with the phone camera or choose an existing file.'
                    : 'Запись прямо на странице в этом браузере недоступна. Можно снять ролик камерой телефона или выбрать готовый файл.'}
                </Alert>
              )}
              {fileButtons}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          {phase === 'review' ? (
            <>
              {confirmDiscard ? (
                <>
                  <Typography variant="body2" sx={{ mr: 'auto' }}>
                    {en ? 'Delete this clip?' : 'Удалить этот клип?'}
                  </Typography>
                  <Button size="small" onClick={() => setConfirmDiscard(false)}>
                    {en ? 'No' : 'Нет'}
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    onClick={discardClip}
                  >
                    {en ? 'Delete' : 'Удалить'}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    color="error"
                    onClick={() => setConfirmDiscard(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    {en ? 'Discard' : 'Удалить'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={keepClip}
                    sx={{ borderRadius: 3, fontWeight: 700 }}
                  >
                    {en ? 'Keep clip' : 'Сохранить клип'}
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              {supported && !error && (
                <Button
                  startIcon={<FolderOpenIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: 'none', mr: 'auto' }}
                >
                  {en ? 'From file' : 'Из файла'}
                </Button>
              )}
              {supported && !error && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<FiberManualRecordIcon />}
                  onClick={startRecording}
                  disabled={phase !== 'ready'}
                  sx={{ borderRadius: 3, fontWeight: 700 }}
                >
                  {en ? 'Start recording' : 'Начать запись'}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {recording && (
        <Paper
          elevation={8}
          role="status"
          sx={{
            position: 'fixed',
            right: 12,
            bottom: floatingBottom,
            zIndex: 1500,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            p: 0.5,
            pr: 1,
            borderRadius: 3,
            bgcolor: 'rgba(17,24,39,0.92)',
            color: '#fff',
          }}
        >
          <Box
            component="video"
            ref={attachLive}
            autoPlay
            muted
            playsInline
            sx={{
              width: 52,
              height: 52,
              borderRadius: 2,
              objectFit: 'cover',
              bgcolor: '#000',
              flexShrink: 0,
            }}
          />
          <FiberManualRecordIcon
            sx={{
              fontSize: 16,
              color: phase === 'paused' ? '#fbbf24' : '#ef4444',
              animation:
                phase === 'recording' ? `${pulse} 1.2s infinite` : 'none',
            }}
          />
          <Typography
            sx={{
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
              minWidth: 40,
              fontSize: '0.95rem',
            }}
          >
            {formatDuration(elapsed)}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.7rem',
                lineHeight: 1.2,
                fontVariantNumeric: 'tabular-nums',
                color: recBytes >= WARN_VIDEO_BYTES ? '#fbbf24' : '#d1d5db',
                fontWeight: recBytes >= WARN_VIDEO_BYTES ? 800 : 400,
              }}
            >
              {formatSize(recBytes)} / 58 {en ? 'MB' : 'МБ'}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.7rem',
                lineHeight: 1.2,
                fontVariantNumeric: 'tabular-nums',
                color: '#d1d5db',
              }}
            >
              {en ? 'left' : 'осталось'} ~{formatDuration(remainingSec)}
            </Typography>
          </Box>
          {canPause && (
            <IconButton
              size="small"
              onClick={togglePause}
              aria-label={
                phase === 'paused'
                  ? en
                    ? 'Resume'
                    : 'Продолжить'
                  : en
                    ? 'Pause'
                    : 'Пауза'
              }
              sx={{ color: '#fff' }}
            >
              {phase === 'paused' ? <PlayArrowIcon /> : <PauseIcon />}
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => stopRecording()}
            aria-label={en ? 'Stop recording' : 'Остановить запись'}
            sx={{
              color: '#fff',
              bgcolor: '#dc2626',
              '&:hover': { bgcolor: '#b91c1c' },
            }}
          >
            <StopIcon />
          </IconButton>
        </Paper>
      )}
    </>
  );
}
